import {
  useEffect,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  MoreHorizontal,
  PaintBucket,
  RemoveFormatting,
  Underline,
} from "lucide-react";
import { useValue } from "@tldraw/state-react";
import type { BoardDocument, BoardTable } from "../shared/schema";
import {
  applyCellStyle,
  normalizeCellRange,
  type CellStyle,
} from "../shared/table-tools";
import {
  getTableInteraction,
  subscribeTableInteraction,
  tableSelection,
} from "./table-interaction";
import type { WorkspaceModel } from "./useWorkspace";
import { notify } from "./api";
import { RichTextLinkControl } from "./RichTextLinkControl";
import { selectedTableStyle } from "./table-style-state";

export function DocumentFormatting({ model }: { model: WorkspaceModel }) {
  const text = useValue(
    "editing document text",
    () => model.editor.getRichTextEditor(),
    [model.editor],
  );
  const documentShape = useValue(
    "editing document shape",
    () => {
      const id = model.editor.getEditingShapeId();
      const shape = id ? model.editor.getShape(id) : null;
      return shape?.type === "document" ? (shape as BoardDocument) : null;
    },
    [model.editor],
  );
  const [sizeDraft, setSizeDraft] = useState("");
  const skipSizeBlur = useRef(false);
  useEffect(
    () => setSizeDraft(String(documentShape?.props.fontSize ?? "")),
    [documentShape?.props.fontSize],
  );
  const commitSize = () => {
    if (!documentShape) return;
    const value = Number(sizeDraft);
    if (
      Number.isFinite(value) &&
      value >= 10 &&
      value <= 72 &&
      value !== documentShape.props.fontSize
    ) {
      model.editor.markHistoryStoppingPoint("Resize document text");
      model.editor.updateShape<BoardDocument>({
        id: documentShape.id,
        type: "document",
        props: { fontSize: value },
      });
    } else setSizeDraft(String(documentShape.props.fontSize));
    text?.view.focus();
  };
  const [, refresh] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    if (!text) return;
    text.on("transaction", refresh);
    return () => {
      text.off("transaction", refresh);
    };
  }, [text]);
  return (
    <div
      className="inline-formatting"
      role="group"
      aria-label="Text formatting"
      onMouseDown={(e) => {
        if ((e.target as Element).closest("button")) {
          const focused = document.activeElement;
          if (
            focused instanceof HTMLInputElement &&
            focused.closest(".calque-doc-size")
          )
            focused.blur();
          e.preventDefault();
        }
      }}
    >
      <select
        aria-label="Text style"
        disabled={!text}
        value={text?.isActive("heading") ? "heading" : "body"}
        onChange={(e) =>
          e.target.value === "heading"
            ? text?.chain().focus().setHeading({ level: 2 }).run()
            : text?.chain().focus().setParagraph().run()
        }
      >
        <option value="body">Body</option>
        <option value="heading">Heading</option>
      </select>
      <label className="calque-doc-size" title="Document font size">
        <span className="sr-only">Document font size</span>
        <input
          aria-label="Document font size"
          type="number"
          min={10}
          max={72}
          value={sizeDraft}
          disabled={!documentShape || !model.canEdit}
          onChange={(event) => setSizeDraft(event.target.value)}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Enter") {
              event.preventDefault();
              event.currentTarget.blur();
            } else if (event.key === "Escape") {
              event.preventDefault();
              skipSizeBlur.current = true;
              setSizeDraft(String(documentShape?.props.fontSize ?? ""));
              event.currentTarget.blur();
            }
          }}
          onBlur={() => {
            if (skipSizeBlur.current) {
              skipSizeBlur.current = false;
              text?.view.focus();
            } else commitSize();
          }}
        />
        <span aria-hidden="true">px</span>
      </label>
      <button
        aria-label="Bold"
        title="Bold"
        disabled={!text}
        aria-pressed={text?.isActive("bold") || false}
        onClick={() => text?.chain().focus().toggleBold().run()}
      >
        <Bold size={17} />
      </button>
      <button
        aria-label="Italic"
        title="Italic"
        disabled={!text}
        aria-pressed={text?.isActive("italic") || false}
        onClick={() => text?.chain().focus().toggleItalic().run()}
      >
        <Italic size={17} />
      </button>
      <label className="inline-color calque-color-field" title="Text color">
        <span className="sr-only">Text color</span>
        <span
          className="calque-color-glyph"
          aria-hidden="true"
          style={{
            borderBottomColor:
              text?.getAttributes("textStyle").color || "#25343b",
          }}
        >
          A
        </span>
        <input
          type="color"
          aria-label="Text color"
          disabled={!text}
          value={text?.getAttributes("textStyle").color || "#25343b"}
          onChange={(e) => text?.chain().focus().setColor(e.target.value).run()}
        />
      </label>
      <RichTextLinkControl rich={text} />
      <details className="format-more">
        <summary
          aria-label="More text formatting"
          title="More formatting"
          onMouseDown={(event) => event.preventDefault()}
        >
          <MoreHorizontal size={18} />
        </summary>
        <div className="format-popover floating-surface">
          <strong className="calque-format-heading">Paragraph</strong>
          <label className="calque-format-field">
            Alignment
            <select
              aria-label="Text alignment"
              disabled={!text}
              value={
                text?.getAttributes(
                  text.isActive("heading") ? "heading" : "paragraph",
                ).textAlign || "left"
              }
              onChange={(e) =>
                text?.chain().focus().setTextAlign(e.target.value).run()
              }
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
              <option value="justify">Justify</option>
            </select>
          </label>
          <button
            disabled={!text}
            aria-pressed={text?.isActive("underline") || false}
            onClick={() => text?.chain().focus().toggleUnderline().run()}
          >
            <Underline size={16} /> Underline
          </button>
          <button
            disabled={!text}
            aria-pressed={text?.isActive("highlight") || false}
            onClick={() => text?.chain().focus().toggleMark("highlight").run()}
          >
            Highlight
          </button>
          <button
            disabled={!text}
            aria-pressed={text?.isActive("bulletList") || false}
            onClick={() => text?.chain().focus().toggleBulletList().run()}
          >
            <List size={16} /> Bullet list
          </button>
          <button
            disabled={!text}
            aria-pressed={text?.isActive("orderedList") || false}
            onClick={() => text?.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered size={16} /> Numbered list
          </button>
          <button
            disabled={!text}
            onClick={() =>
              text?.chain().focus().unsetAllMarks().clearNodes().run()
            }
          >
            <RemoveFormatting size={16} /> Clear formatting
          </button>
        </div>
      </details>
    </div>
  );
}

export function TableFormatting({
  model,
  shape,
}: {
  model: WorkspaceModel;
  shape: BoardTable;
}) {
  const interaction = useSyncExternalStore(
    (fn) => subscribeTableInteraction(shape.id, fn),
    () => getTableInteraction(shape.id),
  );
  const range = normalizeCellRange(tableSelection(shape.id), shape.props.rows);
  const active = normalizeCellRange(
    {
      startRow: interaction.focus.row,
      endRow: interaction.focus.row,
      startColumn: interaction.focus.column,
      endColumn: interaction.focus.column,
    },
    shape.props.rows,
  );
  const selected = selectedTableStyle(shape.meta, shape.props.rows, range);
  const disabled =
    !model.canEdit || model.editor.getIsReadonly() || shape.isLocked;
  const style = (patch: Partial<CellStyle>) => {
    const current = model.editor.getShape<BoardTable>(shape.id);
    if (!current || current.isLocked || model.editor.getIsReadonly()) return;
    try {
      model.editor.markHistoryStoppingPoint("Style table cells");
      model.editor.updateShape({
        id: shape.id,
        type: "table",
        meta: applyCellStyle(current.meta, current.props.rows, range, patch),
      });
    } catch (error) {
      notify((error as Error).message);
    }
  };
  return (
    <div
      className="inline-formatting"
      role="group"
      aria-label="Cell formatting"
      onMouseDown={(e) => {
        if ((e.target as Element).closest("button")) e.preventDefault();
      }}
    >
      <span
        className="cell-range"
        title={`Active R${active.endRow + 1}:C${active.endColumn + 1}; selected R${range.startRow + 1}:C${range.startColumn + 1} to R${range.endRow + 1}:C${range.endColumn + 1}`}
      >
        R{range.startRow + 1}:C{range.startColumn + 1}
        {(range.startRow !== range.endRow ||
          range.startColumn !== range.endColumn) &&
          `–R${range.endRow + 1}:C${range.endColumn + 1}`}
      </span>
      <button
        title="Bold cells"
        aria-label="Bold cells"
        aria-pressed={selected.bold === null ? "mixed" : selected.bold}
        disabled={disabled}
        onClick={() => style({ bold: selected.bold !== true })}
      >
        <Bold size={17} />
      </button>
      <button
        title="Italic cells"
        aria-label="Italic cells"
        aria-pressed={selected.italic === null ? "mixed" : selected.italic}
        disabled={disabled}
        onClick={() => style({ italic: selected.italic !== true })}
      >
        <Italic size={17} />
      </button>
      <label
        className="inline-color calque-color-field"
        title="Cell text color"
      >
        <span className="sr-only">Cell text color</span>
        <span
          className="calque-color-glyph"
          aria-hidden="true"
          style={{ borderBottomColor: selected.textColor ?? "#25343b" }}
        >
          A
        </span>
        <input
          type="color"
          aria-label={
            selected.textColor === null
              ? "Cell text color mixed"
              : "Cell text color"
          }
          disabled={disabled}
          value={selected.textColor ?? "#25343b"}
          onChange={(e) => style({ textColor: e.target.value })}
        />
      </label>
      <label className="inline-color calque-color-field" title="Cell fill">
        <span className="sr-only">Cell fill</span>
        <span
          className="calque-color-glyph"
          aria-hidden="true"
          style={{ borderBottomColor: selected.fill ?? "#ffffff" }}
        >
          <PaintBucket size={16} />
        </span>
        <input
          type="color"
          aria-label="Cell fill"
          disabled={disabled}
          value={selected.fill ?? "#ffffff"}
          onChange={(e) => style({ fill: e.target.value })}
        />
      </label>
      <details className="format-more table-format-more">
        <summary aria-label="More cell formatting" title="More cell formatting">
          <MoreHorizontal size={18} />
        </summary>
        <div className="format-popover floating-surface">
          <strong className="calque-format-heading">Cell style</strong>
          <label className="calque-format-field">
            Font
            <select
              aria-label="Cell font"
              disabled={disabled}
              value={selected.fontFamily ?? ""}
              onChange={(e) =>
                style({ fontFamily: e.target.value as CellStyle["fontFamily"] })
              }
            >
              <option value="" disabled>
                Mixed
              </option>
              <option>Arial</option>
              <option>Georgia</option>
              <option>monospace</option>
            </select>
          </label>
          <label className="calque-format-field">
            Font size
            <input
              aria-label="Cell font size"
              type="number"
              min={10}
              max={32}
              value={selected.fontSize ?? ""}
              placeholder={selected.fontSize === null ? "Mixed" : undefined}
              disabled={disabled}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value >= 10 && value <= 32) style({ fontSize: value });
              }}
            />
          </label>
          <label className="calque-format-field">
            Alignment
            <select
              aria-label="Cell alignment"
              value={selected.align ?? ""}
              disabled={disabled}
              onChange={(e) =>
                style({ align: e.target.value as CellStyle["align"] })
              }
            >
              <option value="" disabled>
                Mixed
              </option>
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </label>
        </div>
      </details>
    </div>
  );
}
